import {
  Button,
  FormField,
  TextInput,
  Avatar,
  AutocompleteNoOptions,
  DropdownItem,
} from '@dev-spendesk/grapes';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';

import { unwrapQuery } from 'src/core/api/unwrapQuery';
import {
  AutocompleteMultipleSearch,
  AutocompleteSearch,
} from 'src/core/common/components/AutocompleteSearch';
import {
  type TGlobalFunctionTyped,
  useTranslation,
} from 'src/core/common/hooks/useTranslation';

import { validateCostCenterForm } from './validate';
import { useOrganisationMembers } from '../../hooks/useOrganisationMembers';
import { type Setting } from '../../setting';

type MemberOption = {
  key: string;
  label: string;
  avatar?: string | null;
};

type Props = {
  onComplete(values: FormValues): void;
  onCancel(): void;
  setting?: Partial<Setting>;
  mode: 'singleModal' | 'stepModal';
};

export type FormValues = {
  name: string | undefined;
  ownerId: string | undefined;
  viewerIds: string[];
  autoAssignFutureEntities: boolean;
};

export const CostCenterDetailsContent = ({
  onComplete,
  onCancel,
  mode,
  setting,
}: Props) => {
  const { t } = useTranslation('global');
  const membersQueryState = useOrganisationMembers();
  const members = unwrapQuery(membersQueryState);

  const reshapedMembers: MemberOption[] =
    members?.map((member) => ({
      key: member.id,
      label: member.isPending ? member.email : member.fullname || member.email,
      avatar: member.avatar,
    })) ?? [];

  const formikProps = useFormik<FormValues>({
    enableReinitialize: false,
    initialValues: {
      name: setting?.name,
      ownerId: setting?.ownerId,
      viewerIds: setting?.viewerIds ?? [],
      autoAssignFutureEntities: setting?.autoAssignFutureEntities ?? false,
    },
    validate: (values) => validateCostCenterForm(values, t, undefined, []),
    onSubmit: async (values) => {
      onComplete(values);
    },
  });

  const selectedOwner = reshapedMembers.find(
    (member) => member.key === formikProps.values.ownerId,
  );

  return (
    <>
      <FormField
        label={t('costCenters.form.name')}
        alertMessage={
          formikProps.errors.name && formikProps.touched.name
            ? formikProps.errors.name
            : undefined
        }
      >
        <TextInput
          name="name"
          value={formikProps.values.name}
          placeholder={t('costCenters.form.namePlaceholder')}
          onChange={formikProps.handleChange}
          onBlur={formikProps.handleBlur}
        />
      </FormField>
      <FormField
        label={t('costCenters.form.owner')}
        alertMessage={
          formikProps.errors.ownerId && formikProps.touched.ownerId
            ? formikProps.errors.ownerId
            : undefined
        }
      >
        <AutocompleteSearch
          name="ownerId"
          fit="parent"
          options={reshapedMembers}
          value={selectedOwner}
          placeholder={t('costCenters.form.owner')}
          isInvalid={Boolean(
            formikProps.errors.ownerId && formikProps.touched.ownerId,
          )}
          renderPrefix={(option) =>
            option && (
              <Avatar
                size={24}
                src={option.avatar ?? undefined}
                text={option.label}
              />
            )
          }
          renderOption={(option, state) => (
            <DropdownItem
              {...state}
              key={option.key}
              label={option.label}
              prefix={
                <Avatar
                  size={24}
                  src={option.avatar ?? undefined}
                  text={option.label}
                />
              }
            />
          )}
          onSelect={(owner) => {
            if (owner) {
              formikProps.setFieldValue('ownerId', owner.key);
              formikProps.setFieldTouched('ownerId', true, false);
            }
          }}
          renderNoOptions={(rawValue) => (
            <AutocompleteNoOptions className="text-center body-m">
              <div>
                <Trans
                  i18nKey="misc.noOptions"
                  values={{ account: rawValue }}
                  components={[
                    <span key="complementary" className="text-content-primary" />,
                  ]}
                />
              </div>
            </AutocompleteNoOptions>
          )}
        />
      </FormField>
      <ViewersField
        viewerOptions={reshapedMembers.filter(
          (member) => member.key !== selectedOwner?.key,
        )}
        selectedViewerIds={formikProps.values.viewerIds}
        onSelectViewers={(viewerIds) =>
          formikProps.setFieldValue('viewerIds', viewerIds)
        }
      />
      {getModalActions({
        onComplete: () => formikProps.submitForm(),
        onCancel,
        step: 'details',
        mode,
        translator: t,
      })}
    </>
  );
};

const ViewersField = ({
  viewerOptions,
  selectedViewerIds,
  onSelectViewers,
}: {
  viewerOptions: MemberOption[];
  selectedViewerIds: string[];
  onSelectViewers: (viewerIds: string[]) => void;
}) => {
  const { t } = useTranslation('global');
  const [sortedViewerOptions, setSortedViewerOptions] =
    useState<MemberOption[]>(viewerOptions);

  useEffect(() => {
    setSortedViewerOptions(
      sortViewersBySelected(viewerOptions, selectedViewerIds),
    );
  }, [viewerOptions, selectedViewerIds]);

  return (
    <FormField
      label={t('costCenters.form.viewers')}
      infoTipContent={t('costCenters.form.viewersInfoText')}
    >
      <AutocompleteMultipleSearch
        name="viewerIds"
        fit="parent"
        options={sortedViewerOptions}
        values={viewerOptions.filter((option) =>
          selectedViewerIds.includes(option.key),
        )}
        placeholder={t('costCenters.form.viewersPlaceholder')}
        translations={{
          selected: t('costCenters.form.selectedMember', {
            count: selectedViewerIds.length,
          }),
        }}
        onSelect={(viewers) =>
          onSelectViewers(viewers.map((viewer) => viewer.key))
        }
        onFocus={() => {
          setSortedViewerOptions(
            sortViewersBySelected(viewerOptions, selectedViewerIds),
          );
        }}
      />
    </FormField>
  );
};

const sortViewersBySelected = (
  memberOptions: MemberOption[],
  viewerIds: string[],
) => {
  return [...memberOptions].sort((a) => {
    if (viewerIds.includes(a.key)) {
      return -1;
    }
    return 1;
  });
};

const getModalActions = ({
  onComplete,
  onCancel,
  step,
  mode,
  translator,
}: {
  onComplete: () => void;
  onCancel: () => void;
  step: 'details' | 'assignment';
  mode: 'singleModal' | 'stepModal';
  translator: TGlobalFunctionTyped;
}) => {
  if (mode === 'singleModal') {
    return (
      <div className="-mb-24 mt-16 flex w-full justify-center gap-16">
        <Button
          key="back"
          variant="secondaryNeutral"
          text={translator(
            'multiEntityHub.costCenters.modal.details.actions.back',
          )}
          onClick={onCancel}
        />
        <Button
          key="submit"
          variant="primaryBrand"
          text={translator(
            'multiEntityHub.costCenters.modal.details.actions.confirm',
          )}
          onClick={onComplete}
        />
      </div>
    );
  }
  if (step === 'details') {
    return (
      <div className="-mb-24 mt-16 flex w-full justify-center gap-16">
        <Button
          key="back"
          variant="secondaryNeutral"
          text={translator(
            'multiEntityHub.costCenters.modal.details.actions.back',
          )}
          onClick={onCancel}
        />
        <Button
          key="submit"
          variant="primaryBrand"
          iconName="chevron-right"
          iconPosition="right"
          text={translator(
            'multiEntityHub.costCenters.modal.details.actions.submit',
          )}
          onClick={onComplete}
        />
      </div>
    );
  }
  return (
    <div className="-mb-24 mt-16 flex w-full justify-center gap-16">
      <Button
        key="back"
        variant="secondaryNeutral"
        text={translator(
          'multiEntityHub.costCenters.modal.assignment.actions.back',
        )}
        onClick={onCancel}
      />
      <Button
        key="submit"
        variant="primaryBrand"
        text={translator(
          'multiEntityHub.costCenters.modal.assignment.actions.submit',
        )}
        onClick={onComplete}
      />
    </div>
  );
};
